export default function PonteVedraBeachDestinationsSection() {
  const stats = [
    {
      value: "62%",
      label: "of Ponte Vedra Beach requests are golf-related or resort travel with under 3 weeks notice",
    },
    {
      value: "3–6 months",
      label: "average planning window for Scotland and Ireland golf trips — passport checks often happen too late",
    },
    {
      value: "Jan–Mar",
      label: "peak golf season prep — passport renewals spike before spring Scotland and Ireland departures",
    },
    {
      value: "48-hour",
      label: "shortest golf trip departure window we've accommodated for a Ponte Vedra Beach group",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "Scotland & Ireland",
      context: "The most common international destination for Ponte Vedra Beach golfers — St. Andrews Old Course, Carnoustie, Royal County Down, Ballybunion, Lahinch, and Old Head draw serious golfers on bucket-list trips every spring and summer. These group outings of 8–16 players are typically booked 6–12 months in advance through package tour operators, but passport validity checks often happen only when final payments are due.",
      urgency: "Golf package trips require full payment 60–90 days before departure — passport issues discovered at final payment can derail a $15,000+ group trip",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "The most common leisure destination for Ponte Vedra Beach families — accessed via Carnival cruises from JAXPORT (4–5 day Bahamas itineraries) or longer Caribbean cruises from Port Canaveral and Fort Lauderdale. Royal Caribbean, Norwegian, Celebrity, and Disney all operate itineraries that attract Ponte Vedra Beach's affluent demographic during school breaks and the winter golf off-season.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "United Kingdom & London",
      context: "London, Edinburgh, and Manchester host major medical conferences that draw Mayo Clinic Jacksonville physicians and researchers from Ponte Vedra Beach. The Royal College of Physicians, European Society of Cardiology, and oncology conferences are fixtures on the Ponte Vedra Beach medical travel calendar. Conference abstracts accepted 2–4 weeks before events create compressed travel timelines.",
      urgency: "Medical conference acceptance notices arrive 2–4 weeks before the event — insufficient time for standard passport processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada",
      context: "Canada is a popular summer destination for Ponte Vedra Beach families — Banff and Whistler for hiking and golf, Toronto and Montreal for cultural city breaks, and Vancouver Island for coastal scenery. Golfers also travel to Cabot Links and Cabot Cliffs in Nova Scotia, which rank among the world's top courses. Seasonal residents often combine Canadian summer travel with their northward migration.",
      urgency: "Summer golf trips to Cabot Links and family vacations to Banff are typically booked in spring — passport checks should happen at booking, not at departure",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico & Latin America",
      context: "Cancun, Riviera Maya, and Los Cabos are staple beach destinations for Ponte Vedra Beach families seeking warm-weather getaways within a short flight. JAX connects through Atlanta, Charlotte, and Houston for direct Mexico service. Mexico requires six months of passport validity for entry — a rule that catches many travelers who assume a valid passport is sufficient regardless of expiration proximity.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February and March for Ponte Vedra Beach families",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Europe — France, Italy & Spain",
      context: "Paris, Rome, Barcelona, and the Amalfi Coast draw Ponte Vedra Beach families for summer cultural immersion and multi-generational trips. European golf destinations like France's Le Golf National (Ryder Cup venue) and Italy's Marco Simone also attract Ponte Vedra Beach golfers. These trips are typically planned 6–12 months in advance with significant financial commitment.",
      urgency: "European trips require substantial deposits 90–120 days in advance — passport issues discovered at deposit time can cost thousands in cancellation fees",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Ponte Vedra Beach Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Ponte Vedra Beach
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Ponte Vedra Beach travelers head to Scotland and Ireland for golf, the Caribbean for cruises and resorts, Europe for medical conferences and cultural trips, and Canada for summer golf and family travel. These are the destinations that create the most passport urgency for our Ponte Vedra Beach clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout Florida and the Southeast</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Jacksonville", href: "/cities/jacksonville" },
              { name: "St. Augustine", href: "/st-augustine" },
              { name: "Palm Beach", href: "/cities/palm-beach" },
              { name: "Palm Beach Gardens", href: "/cities/palm-beach-gardens" },
              { name: "Jupiter", href: "/cities/jupiter" },
              { name: "West Palm Beach", href: "/cities/west-palm-beach" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}